// ==================== CHECKBOX COMPONENT ====================

import { forwardRef, useId, useState, useCallback } from 'react';
import { Check, Minus } from 'lucide-react';
import { cn } from '../../utils/helpers';

interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: React.ReactNode;
  description?: string;
  error?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'primary' | 'rounded';
  indeterminate?: boolean;
}

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      label,
      description,
      error,
      size = 'md',
      variant = 'primary',
      indeterminate = false,
      className,
      disabled,
      checked: controlledChecked,
      defaultChecked,
      onChange,
      id,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const checkboxId = id || generatedId;
    
    // Track internal state for uncontrolled mode
    const [internalChecked, setInternalChecked] = useState(defaultChecked ?? false);
    
    // Determine if controlled or uncontrolled
    const isControlled = controlledChecked !== undefined;
    const isCheckedState = isControlled ? controlledChecked : internalChecked;
    
    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      if (!isControlled) {
        setInternalChecked(e.target.checked);
      }
      onChange?.(e);
    }, [isControlled, onChange]);

    const sizes = {
      sm: {
        box: 'w-4 h-4',
        icon: 12,
        label: 'text-sm',
      },
      md: {
        box: 'w-5 h-5',
        icon: 14,
        label: 'text-sm',
      },
      lg: {
        box: 'w-6 h-6',
        icon: 16,
        label: 'text-base',
      },
    };

    const variants = {
      default: 'rounded',
      primary: 'rounded',
      rounded: 'rounded-full',
    };

    const isChecked = isCheckedState || indeterminate;

    return (
      <div className={cn('relative', className)}>
        <label
          htmlFor={checkboxId}
          className={cn(
            'inline-flex items-start gap-3',
            disabled ? 'cursor-not-allowed' : 'cursor-pointer'
          )}
        >
          {/* Hidden Checkbox */}
          <input
            ref={ref}
            type="checkbox"
            id={checkboxId}
            disabled={disabled}
            checked={isCheckedState}
            onChange={handleChange}
            className="sr-only peer"
            {...props}
          />

          {/* Custom Checkbox */}
          <div
            className={cn(
              'relative shrink-0 border-2 transition-all duration-200 flex items-center justify-center',
              sizes[size].box,
              variants[variant],
              isChecked
                ? 'bg-(--color-signal-orange) border-(--color-signal-orange)'
                : 'bg-transparent border-(--color-border-gray) hover:border-(--color-signal-orange)/50',
              disabled && 'opacity-50',
              error && !isChecked && 'border-(--color-error)'
            )}
          >
            {/* Check Icon */}
            {isCheckedState && !indeterminate && (
              <Check
                size={sizes[size].icon}
                className="text-white"
                strokeWidth={3}
              />
            )}

            {/* Indeterminate Icon */}
            {indeterminate && (
              <Minus
                size={sizes[size].icon}
                className="text-white"
                strokeWidth={3}
              />
            )}
          </div>

          {/* Label & Description */}
          {(label || description) && (
            <div className="flex flex-col pt-0.5">
              {label && (
                <span
                  className={cn(
                    'font-medium',
                    sizes[size].label,
                    disabled ? 'text-(--color-muted-gray)' : 'text-(--color-soft-gray)'
                  )}
                >
                  {label}
                  {props.required && (
                    <span className="text-(--color-error) ml-1">*</span>
                  )}
                </span>
              )}
              {description && (
                <span className="text-xs text-(--color-muted-gray) mt-0.5">
                  {description}
                </span>
              )}
            </div>
          )}
        </label>

        {/* Error */}
        {error && (
          <p className="mt-1 text-sm text-(--color-error) ml-8">{error}</p>
        )}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';

export { Checkbox };

